import { call, put, takeLatest } from "redux-saga/effects";
import * as types from "../actions/actionTypes";
import { InitAction, ProjectAction, UserManagementAction } from "../actions";
import * as userManagementApi from "../api/userManagement";
import { toastr } from "react-redux-toastr";
import { SetMessage } from "../assets/errorMessages";
import { go, goBack } from "connected-react-router";
import { GO_BACK_COUNTER, PROJECT_ID } from "../constants/storageKeys";
import * as initApi from "../api/init";

export function* watcherUserManagement() {
  yield takeLatest(types.USER_MANAGEMENT_GET_USER_LIST_REQUEST, userList);
  yield takeLatest(
    types.USER_MANAGEMENT_CHECK_PHONE_NUMBER_REQUEST,
    checkPhoneNumber
  );
  yield takeLatest(types.USER_MANAGEMENT_INVITE_REQUEST, invite);
  yield takeLatest(types.USER_MANAGEMENT_INIT_INVITE_REQUEST, initInvite);
  yield takeLatest(
    types.USER_MANAGEMENT_SHOW_USER_PROFILE_REQUEST,
    showUserProfile
  );
  yield takeLatest(
    types.USER_MANAGEMENT_SHOW_PROJECT_PROFILE_REQUEST,
    showProjectProfile
  );
  yield takeLatest(
    types.USER_MANAGEMENT_SHOW_PROJECT_USER_PROFILE_REQUEST,
    showProjectUserProfile
  );
  yield takeLatest(
    types.USER_MANAGEMENT_CONVERT_ADMIN_TO_MANAGER_REQUEST,
    convertAdminToManager
  );
  yield takeLatest(
    types.USER_MANAGEMENT_CONVERT_MANAGER_TO_ADMIN_REQUEST,
    convertManagerToAdmin
  );
  yield takeLatest(types.USER_MANAGEMENT_REACTIVATE_REQUEST, reactivate);
  yield takeLatest(types.USER_MANAGEMENT_DEACTIVATE_REQUEST, deactivate);
  yield takeLatest(
    types.USER_MANAGEMENT_DELETE_INVITATION_REQUEST,
    deleteInvitation
  );
  yield takeLatest(
    types.USER_MANAGEMENT_REMOVE_MANAGER_FROM_TEAM_REQUEST,
    removeManagerFromTeam
  );
  yield takeLatest(
    types.USER_MANAGEMENT_INVITE_MANAGER_TO_OTHER_TEAMS_REQUEST,
    inviteManagerToOtherTeams
  );
  yield takeLatest(
    types.USER_MANAGEMENT_CHANGE_TEAM_PERMISSIONS_REQUEST,
    changeTeamPermissions
  );
  yield takeLatest(
    types.USER_MANAGEMENT_GET_USER_ACCOUNTING_CODES_REQUEST,
    getUserAccountingCodes
  );
  yield takeLatest(
    types.USER_MANAGEMENT_EDIT_USER_ACCOUNTING_CODES_REQUEST,
    editUserAccountingCodes
  );
  yield takeLatest(
    types.USER_MANAGEMENT_GET_USER_IMPREST_PAYMENT_REQUEST,
    getUserImprestPaymentLimit
  );
  yield takeLatest(
    types.USER_MANAGEMENT_EDIT_IMPREST_PAYMENT_LIMIT_REQUEST,
    editUserImprestPaymentLimit
  );
  yield takeLatest(
    types.USER_MANAGEMENT_GET_SHARED_PROJECTS_REQUEST,
    sharedProjects
  );
}

function fetchUserList(data) {
  return userManagementApi.userList(data);
}

function* userList(action) {
  try {
    const response = yield call(fetchUserList, action.data);
    yield put(UserManagementAction.getUserListSuccess(response.data.data));
  } catch (error) {
    const message = SetMessage(error);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.getUserListFailure(message));
  }
}

function fetchCheckPhoneNumber(data) {
  return userManagementApi.checkPhoneNumber(data);
}

function* checkPhoneNumber(action) {
  try {
    const response = yield call(fetchCheckPhoneNumber, action.data);
    response.data.message.forEach(message => {
      toastr.success(" ", message.toString());
    });
    yield put(
      UserManagementAction.checkPhoneNumberSuccess(
        response.data,
        action.data.phone_number
      )
    );
  } catch (error) {
    SetMessage(error, true);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.checkPhoneNumberFailure());
  }
}

function fetchInvite(data) {
  return userManagementApi.invite(data);
}

function* invite(action) {
  try {
    const { goBackCount, ...passedData } = action.data;
    const response = yield call(fetchInvite, passedData);
    yield call(
      handleExtraReqAndUpdate,
      passedData.project_id, //projectId
      response.data.user.id, //userId
      passedData.updateProjectUserProfile, //updateProjectUserProfile
      UserManagementAction.inviteSuccess, //mainActionSuccess
      response, //mainActionResponse
      goBackCount //goBackCount
    );
  } catch (error) {
    SetMessage(error, true);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.inviteFailure());
  }
}

function fetchInitInvite(data) {
  return userManagementApi.initInvite(data);
}

function* initInvite(action) {
  try {
    const response = yield call(fetchInitInvite, action.data);
    response.data.message.forEach(message => {
      toastr.success(" ", message.toString());
    });
    if (response.data.premium) {
      yield put(
        InitAction.getSuccess({ updatePremium: response.data.premium })
      );
    }
    yield put(UserManagementAction.initInviteSuccess(response.data));
  } catch (error) {
    const message = SetMessage(error);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.initInviteFailure(message));
  }
}

function fetchShowUserProfile(userId) {
  return userManagementApi.showUserProfile(userId);
}

function* showUserProfile(action) {
  try {
    const response = yield call(fetchShowUserProfile, action.userId);
    const data = response.data.data;
    if (data.active.length + data.pending.length + data.inactive.length === 0) {
      yield put(goBack());
    } else {
      yield put(UserManagementAction.showUserProfileSuccess(data));
    }
  } catch (error) {
    const message = SetMessage(error);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.showUserProfileFailure(message));
  }
}

function fetchShowProjectProfile(projectId) {
  return userManagementApi.showProjectProfile(projectId);
}

function* showProjectProfile(action) {
  try {
    const response = yield call(fetchShowProjectProfile, action.projectId);
    yield put(
      UserManagementAction.showProjectProfileSuccess(response.data.data)
    );
  } catch (error) {
    const message = SetMessage(error);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.showProjectProfileFailure(message));
  }
}

function fetchShowProjectUserProfile(userId, projectId) {
  return userManagementApi.showProjectUserProfile(userId, projectId);
}

function* showProjectUserProfile(action) {
  try {
    const response = yield call(
      fetchShowProjectUserProfile,
      action.userId,
      action.projectId
    );
    if (response.data.data.premium) {
      yield put(
        InitAction.getSuccess({ updatePremium: response.data.data.premium })
      );
    }
    yield put(
      UserManagementAction.showProjectUserProfileSuccess(response.data.data)
    );
  } catch (error) {
    const message = SetMessage(error);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.showProjectUserProfileFailure(message));
  }
}

function convertAdminToManagerRequest(data) {
  return userManagementApi.convertAdminToManager(data);
}

function* convertAdminToManager(action) {
  try {
    const response = yield call(convertAdminToManagerRequest, action.data);
    response.data.message.forEach(message => {
      toastr.success(" ", message.toString());
    });
    yield put(UserManagementAction.convertAdminToManagerSuccess());
    const goBackCount = parseInt(localStorage.getItem(GO_BACK_COUNTER));
    yield put(go(-goBackCount));
  } catch (error) {
    SetMessage(error, true);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.convertAdminToManagerFailure());
  }
}

function convertManagerToAdminRequest(data) {
  return userManagementApi.convertManagerToAdmin(data);
}

function* convertManagerToAdmin(action) {
  try {
    const response = yield call(convertManagerToAdminRequest, action.data);
    yield call(
      handleExtraReqAndUpdate,
      action.data.project_id, //projectId
      action.data.user_id, // userId
      true, //updateProjectUserProfile
      UserManagementAction.convertManagerToAdminSuccess, //mainActionSuccess
      response //mainActionResponse
    );
  } catch (error) {
    SetMessage(error, true);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.convertManagerToAdminFailure());
  }
}

function reactivateRequest(data) {
  return userManagementApi.reactivate(data);
}

function* reactivate(action) {
  try {
    const response = yield call(reactivateRequest, action.data);
    yield call(
      handleExtraReqAndUpdate,
      action.data.project_id, //projectId
      action.data.user_id, //userId
      action.data.updateProjectUserProfile, //updateProjectUserProfile
      UserManagementAction.reactivateSuccess, //mainActionSuccess
      response, //mainActionResponse
      parseInt(localStorage.getItem(GO_BACK_COUNTER))
    );
  } catch (error) {
    SetMessage(error, true);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.reactivateFailure());
  }
}

function deactivateRequest(data) {
  return userManagementApi.deactivate(data);
}

function* deactivate(action) {
  try {
    const response = yield call(deactivateRequest, action.data);
    yield call(
      handleExtraReqAndUpdate,
      action.data.project_id, //projectId
      action.data.user_id, //userId
      true, //updateProjectUserProfile
      UserManagementAction.deactivateSuccess, //mainActionSuccess
      response //mainActionResponse
    );
  } catch (error) {
    SetMessage(error, true);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.deactivateFailure());
  }
}

function deleteInvitationRequest(data) {
  return userManagementApi.deleteInvitation(data);
}

function* deleteInvitation(action) {
  try {
    const response = yield call(deleteInvitationRequest, action.data);
    yield call(
      handleExtraReqAndUpdate,
      action.data.project_id, //projectId
      action.data.user_id, //userId
      true, //updateProjectUserProfile
      UserManagementAction.deleteInvitationSuccess, //mainActionSuccess
      response //mainActionResponse
    );
  } catch (error) {
    SetMessage(error, true);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.deleteInvitationFailure());
  }
}

function removeManagerFromTeamRequest(data) {
  return userManagementApi.removeManagerFromTeam(data);
}

function* removeManagerFromTeam(action) {
  try {
    const response = yield call(removeManagerFromTeamRequest, action.data);

    yield call(
      handleExtraReqAndUpdate,
      action.data.project_id, //projectId
      action.data.user_id, //userId
      true, //updateProjectUserProfile
      UserManagementAction.removeManagerFromTeamSuccess, //mainActionSuccess
      response //mainActionResponse
    );
  } catch (error) {
    SetMessage(error, true);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.removeManagerFromTeamFailure());
  }
}

function inviteManagerToOtherTeamsRequest(data) {
  return userManagementApi.inviteManagerToOtherTeams(data);
}

function* inviteManagerToOtherTeams(action) {
  try {
    const response = yield call(inviteManagerToOtherTeamsRequest, action.data);
    yield call(
      handleExtraReqAndUpdate,
      action.data.project_id, //projectId
      null, //userId
      false, //updateProjectUserProfile
      UserManagementAction.inviteManagerToOtherTeamsSuccess, //mainActionSuccess
      response, //mainActionResponse
      parseInt(localStorage.getItem(GO_BACK_COUNTER)) //goBackCount
    );
  } catch (error) {
    SetMessage(error, true);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.inviteManagerToOtherTeamsFailure());
  }
}

function changeTeamPermissionsRequest(data) {
  return userManagementApi.changeTeamPermissions(data);
}

function* changeTeamPermissions(action) {
  try {
    const response = yield call(changeTeamPermissionsRequest, action.data);
    yield call(
      handleExtraReqAndUpdate,
      action.data.project_id, //projectId
      null, //userId
      false, //updateProjectUserProfile
      UserManagementAction.deactivateSuccess, //mainActionSuccess
      response, //mainActionResponse
      1 //goBackCount
    );
  } catch (error) {
    SetMessage(error, true);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.changeTeamPermissionsFailure());
  }
}

function getUserAccountingCodesRequest(data) {
  return userManagementApi.getUserAccountingCodes(data);
}

function* getUserAccountingCodes(action) {
  try {
    const response = yield call(getUserAccountingCodesRequest, action.data);
    yield put(
      UserManagementAction.getUserAccountingCodesSuccess(response.data)
    );
  } catch (error) {
    const message = SetMessage(error);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.getUserAccountingCodesFailure(message));
  }
}

function editUserAccountingCodesRequest(data) {
  return userManagementApi.editUserAccountingCodes(data);
}

function* editUserAccountingCodes(action) {
  try {
    const response = yield call(editUserAccountingCodesRequest, action.data);
    response.data.message.forEach(message => {
      toastr.success(" ", message.toString());
    });
    yield put(UserManagementAction.editUserAccountingCodesSuccess());
    yield put(goBack());
  } catch (error) {
    SetMessage(error, true);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.editUserAccountingCodesFailure());
  }
}

function getUserImprestPaymentLimitRequest(data) {
  return userManagementApi.getUserImprestPaymentLimit(data);
}

function* getUserImprestPaymentLimit(action) {
  try {
    const response = yield call(getUserImprestPaymentLimitRequest, action.data);
    yield put(
      UserManagementAction.getUserImprestPaymentLimitSuccess(response.data)
    );
  } catch (error) {
    const message = SetMessage(error);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.getUserImprestPaymentLimitFailure(message));
  }
}

function editUserImprestPaymentLimitRequest(data) {
  return userManagementApi.editUserImprestPaymentLimit(data);
}

function* editUserImprestPaymentLimit(action) {
  try {
    const response = yield call(
      editUserImprestPaymentLimitRequest,
      action.data
    );
    response.data.message.forEach(message => {
      toastr.success(" ", message.toString());
    });
    yield put(UserManagementAction.editUserImprestPaymentLimitSuccess());
    yield put(goBack());
  } catch (error) {
    SetMessage(error, true);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.editUserImprestPaymentLimitFailure());
  }
}

function fetchSharedProjects() {
  return userManagementApi.getSharedProjects();
}

function* sharedProjects() {
  try {
    const response = yield call(fetchSharedProjects);
    yield put(
      UserManagementAction.getSharedProjectsSuccess(response.data.data)
    );
  } catch (error) {
    const message = SetMessage(error);
    // dispatch a failure action to the store with the error
    yield put(UserManagementAction.getSharedProjectsFailure(message));
  }
}

function fetchInit() {
  return initApi.init();
}

function* handleExtraReqAndUpdate(
  projectId,
  userId,
  updateProjectUserProfile,
  mainActionSuccess,
  mainActionResponse,
  goBackCount = 0
) {
  try {
    const mainActionResponseData = mainActionResponse.data;
    try {
      // get init again
      const currentProjectId = localStorage.getItem(PROJECT_ID);
      if (parseInt(currentProjectId) === parseInt(projectId)) {
        const response = yield call(fetchInit);
        const data = response.data;
        yield put(InitAction.getSuccess(data));
      } else if (mainActionResponseData.premium) {
        yield put(
          InitAction.getSuccess({
            updatePremium: mainActionResponseData.premium
          })
        );
      }
    } catch (error) {
      const message = SetMessage(error);
      yield put(InitAction.changeServerInterruptStatus(message));
      yield put(InitAction.getFailure(message));
    }

    mainActionResponseData.message.forEach(message => {
      toastr.success(" ", message.toString());
    });
    // yield put(mainActionSuccess());
    // if (goBackCount) {
    //   yield put(go(-goBackCount));
    // }
    yield put(UserManagementAction.closeConfirm());
    if (updateProjectUserProfile) {
      try {
        // get projectUserProfile again
        yield put(UserManagementAction.showProjectUserProfileRequestClone());
        const response = yield call(
          fetchShowProjectUserProfile,
          userId,
          projectId
        );
        const updatedData = response.data.data;
        if (updatedData.project_users.length === 0) {
          // yield put(goBack());
          goBackCount += 1;
        } else {
          yield put(
            UserManagementAction.showProjectUserProfileSuccess(updatedData)
          );
        }
        yield put(mainActionSuccess());
        if (goBackCount) {
          yield put(go(-goBackCount));
        }
      } catch (error) {
        const message = SetMessage(error);
        yield put(UserManagementAction.showProjectUserProfileFailure(message));
      }
    } else {
      yield put(mainActionSuccess());
      if (goBackCount) {
        yield put(go(-goBackCount));
      }
    }
    yield put(ProjectAction.clearList());
  } catch (error) {
    throw error;
  }
}
